import React, { useContext, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { LiliButton } from '@lili/lili-frontend-common';
import { useTranslation } from 'react-i18next';
import Heading from '../../../../generic-components/Heading';
import { ToggleButton } from './ToggleButton';
import Button from '../../../../generic-components/Button';
import { useToggleSavingStateMutation } from '../../Hooks/useToggleSavingStateMutation';
import { ReactComponent as SetUpIcon } from '../../../../assets/svg/autosavings-blue.svg';
import { NUMBER_TO_DAY_MAP } from '../../services';
import { FlowContext } from '../../context';
import { STEPS } from '../../consts';
import { ISavingsBucket } from '../../interfaces';
import { USER_MESSAGE_TYPES } from '../../../../flows/UserMessageFlow/constants';
import { POPUP_LIFESPAN } from '../../../../flows/emergencyBucketDashboard/constants';
import { useStore } from '../../../../store';

interface IProps {
  bucketState: ISavingsBucket;
  onRefreshBucketState: () => void;
  isDashboardLoading: boolean;
}

export const SavingsController = ({ bucketState, onRefreshBucketState, isDashboardLoading }: IProps) => {
  const isAutoSaveActive = useMemo(() => bucketState?.available && bucketState?.autoSave, [bucketState]);
  const context = useContext(FlowContext);
  const { setMessageObject } = useStore((state) => state);
  const { t } = useTranslation();

  const isRequireSetupSavings = useMemo(() => !bucketState?.accountId || !bucketState?.amount, [bucketState]);

  const [localToggleState, setLocalToggleState] = useState<boolean>(isAutoSaveActive || false);

  /** Hooks */
  const { mutateAsync: updateSavingBucketStatus, isLoading: isUpdatingBucketStatus } = useToggleSavingStateMutation();
  /** --- */

  const handleEditBucketValue = () => {
    context?.changeActiveStep(STEPS.SETUP);
  };

  const handleToggle = (status: boolean) => {
    if (isRequireSetupSavings) {
      handleEditBucketValue();
      return;
    }
    setLocalToggleState(status);
    updateSavingBucketStatus({ autoSaveStatus: status })
      // In-case toggle API failed, revert the toggle back to original position
      .then(() => {
        if (status) {
          setMessageObject({
            type: USER_MESSAGE_TYPES.UPPER_MESSAGE,
            useLottie: true,
            iconSource: '',
            messageText: `${t('SAVINGS_BUCKET.SAVINGS_CONTROLLER.UM_PRE')} ${context.isSavingsBucket ? t('EMERGENCY_BUCKET_HEADER_PRO') : t('EMERGENCY_BUCKET_HEADER_BASIC')} ${t('SAVINGS_BUCKET.SAVINGS_CONTROLLER.UM_POST')}`,
            autoCloseTimeout: POPUP_LIFESPAN,
            callback: () => {
              setMessageObject({ type: USER_MESSAGE_TYPES.ZERO_STATE });
            },
          });
        }
        onRefreshBucketState();
      })
      .catch(() => setLocalToggleState(!status));
  };

  return (
    <div className='flex flex-col'>
      <header className='flex items-center justify-between px-[40px] py-[30px] border-b border-bw03'>
        <Heading
          title={t('SAVINGS_BUCKET.SAVINGS_CONTROLLER.AUTO_SAVING')}
          className='font-metricR text-[16px] text-bw08'
        />
        <ToggleButton
          isLoading={isUpdatingBucketStatus || isDashboardLoading}
          onToggleStatusChange={handleToggle}
          defaultState={isAutoSaveActive}
        />
      </header>
      {localToggleState ? (
        <div className='py-[35px] px-[30px] flex items-start gap-[24px]'>
          <div className='flex flex-col min-w-fit'>
            <div className='flex items-start gap-[2px]'>
              <Heading title='$' className='font-metricR text-[20px] text-action03' />
              <Heading
                title={bucketState?.weeklyAmount.toString()}
                className='font-metricR text-[40px] leading-[45px] text-action03'
              />
            </div>
            <Heading
              title={t('SAVINGS_BUCKET.SAVINGS_CONTROLLER.FEE_PERIOD')}
              className='font-metricR text-[16px] text-action03'
            />
          </div>

          <div className='flex flex-col items-start'>
            <Heading
              title={`${t('SAVINGS_BUCKET.SAVINGS_CONTROLLER.DISCLAIMER_PART_1')} 
              $${bucketState?.weeklyAmount} ${t('SAVINGS_BUCKET.SAVINGS_CONTROLLER.DISCLAIMER_PART_2')} ${context.isSavingsBucket ? t('SAVINGS_BUCKET.SAVINGS_CONTROLLER.SAVINGS') : t('SAVINGS_BUCKET.SAVINGS_CONTROLLER.BUCKET')} ${t('SAVINGS_BUCKET.SAVINGS_CONTROLLER.DISCLAIMER_PART_3')} ${NUMBER_TO_DAY_MAP[bucketState?.dayOfWeek as keyof typeof NUMBER_TO_DAY_MAP]}.`}
              className='text-[16px] text-bw07 font-metricR mb-[33px]'
            />
            <Button
              disable={isUpdatingBucketStatus}
              onClick={handleEditBucketValue}
              className='font-metricR text-action03 text-[14px] underline hover:text-action02'
              title={t('SAVINGS_BUCKET.SAVINGS_CONTROLLER.EDIT')}
              id='editBucketValue'
            />
          </div>
        </div>
      ) : (
        <div className='py-[24px] px-[30px] flex items-start gap-[24px]'>
          <SetUpIcon />
          <div className='flex flex-col items-start gap-[24px]'>
            <Heading
              title={t('SAVINGS_BUCKET.SAVINGS_CONTROLLER.TURN_ON_SAVINGS_HEADER')}
              className='font-metricR text-[16px] text-bw07'
            />
            {isUpdatingBucketStatus ? (
              <Skeleton width='233px' height='52px' />
            ) : (
              <LiliButton
                disabled={isUpdatingBucketStatus}
                onClick={() => handleToggle(true)}
                className='w-[230px]'
                title={t('SAVINGS_BUCKET.SAVINGS_CONTROLLER.TURN_ON_CTA')}
                id='turnOnSavingsCta'
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
